import { css, cx } from "@emotion/css";
import { OctopusCogIcon, OctopusIcon, PlatformHubIcon, UserIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, fontFamily, fontWeight, themeTokens } from "@octopusdeploy/design-system-tokens";
import React from "react";
import { AvatarFallbackProjectIcon, AvatarFallbackTenantIcon } from "./FallbackIcons";
import { generateFallbackText, getIconSize, useCanLoadImage } from "./utils";
export type AvatarSize = "large" | "medium" | "small";
type AvatarType = "user" | "space" | "project" | "tenant" | "template";
interface AvatarBaseProps {
    /** The size of the avatar (small: 20, medium: 36, large: 48) */
    size: AvatarSize;
    /** The type of the avatar (user, space, project, tenant, template) */
    type: AvatarType;
    /** The URL to navigate to when the avatar is clicked */
    href?: string;
    /** Whether the avatar is editable (shows an "edit" overlay) */
    hasEditOverlay?: boolean;
    /** Whether the avatar is visually muted*/
    isMuted?: boolean;
    /** An optional title for the avatar, useful for when no additional text is provided will provide a native tooltip on hover */
    title?: string;
}
export interface AvatarUserProps extends AvatarBaseProps, AvatarImageProps {
    type: "user";
    /**
     * The text to use for the avatar (will be truncated to 2 characters)
     * this is the default if the image fails or is not provided
     */
    text: string;
    /**
     * Determines if the user is a service account (https://octopus.com/docs/security/users-and-teams/service-accounts)
     * A different fallback icon is applied to service accounts to help distinguish between them and regular user accounts.
     */
    isServiceUser?: boolean;
}
export interface AvatarImageProps extends AvatarBaseProps {
    /**
     * The src to use for the avatar image
     */
    src?: string;
    /**
     * The alt text for the image
     */
    alt?: string;
}
export interface AvatarSpaceProps extends AvatarImageProps, AvatarBaseProps {
    type: "space";
}
export interface AvatarTenantProps extends AvatarImageProps, AvatarBaseProps {
    type: "tenant";
}
export interface AvatarProjectProps extends AvatarImageProps, AvatarBaseProps {
    type: "project";
}
export interface AvatarTemplateProps extends AvatarImageProps, AvatarBaseProps {
    type: "template";
}
export type AvatarProps = AvatarUserProps | AvatarSpaceProps | AvatarTenantProps | AvatarProjectProps | AvatarTemplateProps;
function isAvatarType<T extends AvatarProps>(props: AvatarProps, type: AvatarType): props is T {
    return props.type === type;
}
/**
 * Avatar Component
 * Please refer to the <a href="https://www.octopus.design/latest/components/images-and-icons/avatar/overview-ssKEQ1w5" target="_blank">Avatar Design System documentation</a> for more information.
 *
 * @param {AvatarProps} props - The properties for the avatar component. AvatarProps can be one of the following: AvatarUserProps, AvatarSpaceProps, AvatarTenantProps, or AvatarProjectProps.
 * @param {AvatarSize} props.size - The size of the avatar (small, medium, large).
 * @param {AvatarType} props.type - Accepts a `type` prop to determine which avatar to render.
 * - user: Renders a user avatar with an image or fallback text. If the `isServiceUser` prop is true, an icon as the fallback (Octopus logo with a cog inside) to indicate the user is a service account.
 * - space: Renders a space avatar with an image or Octopus icon.
 * - project: Renders a project avatar with an image or fallback project icon (Rocket).
 * - tenant: Renders a tenant avatar with an image or fallback tenant icon.
 * @param {string} [props.href] - The URL to navigate to when the avatar is clicked.
 * @param {boolean} [props.hasEditOverlay] - Displays an "edit overlay" of the avatar to indicate you can edit the avatar, a href must be provided to link to the edit page.
 * @param {boolean} [props.isMuted] - Determines if the avatar is muted.
 * @param {string} [props.src] - The source URL for the avatar image.
 * @param {string} [props.alt] - The alt text for the avatar image.
 * @param {string} [props.text] - For Type: User, The text to display if no image is provided, truncated to 2 characters.
 * @param {boolean} [props.isServiceUser] - For Type: User, Determines if the user is a service account, which changes the fallback icon.
 * @param {string} [props.title] -  An optional title for the avatar, useful for when no additional text is provided will provide a native tooltip on hover
 * @returns {React.ReactElement} A React element representing the avatar.
 */
export function Avatar(props: AvatarProps): React.ReactElement | null {
    if (isAvatarType<AvatarUserProps>(props, "user")) {
        return <AvatarUser {...props}/>;
    }
    if (isAvatarType<AvatarSpaceProps>(props, "space")) {
        return <AvatarSpace {...props}/>;
    }
    if (isAvatarType<AvatarTenantProps>(props, "tenant")) {
        return <AvatarTenant {...props}/>;
    }
    if (isAvatarType<AvatarProjectProps>(props, "project")) {
        return <AvatarProject {...props}/>;
    }
    if (isAvatarType<AvatarTemplateProps>(props, "template")) {
        return <AvatarTemplate {...props}/>;
    }
    return null;
}
/**
 * Generates the avatar image URL and `srcSet` for different resolutions.
 * Adds query parameters to handle image size and fallback behavior.
 *
 * @param {string} src - The base URL for the avatar image.
 * @param {AvatarSize} size - The size of the avatar (small, medium, large).
 * @returns {{ avatarImageUrl: string; srcSet: string }} The image URL and `srcSet` for responsive images.
 */
function getAvatarImageUrls(src: string, size: AvatarSize): {
    avatarImageUrl: string;
    srcSet: string;
} {
    const avatarImageSize = avatarSizeMap[size];
    try {
        const url = new URL(src);
        url.searchParams.set("d", "404");
        url.searchParams.set("s", avatarImageSize.toString());
        const avatarImageUrl = url.toString();
        const srcSet = [1, 2, 3]
            .map((scale) => {
            const scaledUrl = new URL(url.toString());
            scaledUrl.searchParams.set("s", (avatarImageSize * scale).toString());
            return `${scaledUrl.toString()} ${scale}x`;
        })
            .join(", ");
        return { avatarImageUrl, srcSet };
    }
    catch (error) {
        return { avatarImageUrl: src, srcSet: "" };
    }
}
/**
 * Wraps the avatar in a clickable link or a static container.
 * Displays an "edit" overlay if `hasEditOverlay` is true.
 *
 * @param {object} props - The component props.
 * @param {string} [props.href] - The URL to navigate to when the avatar is clicked.
 * @param {string} props.className - The CSS class for styling the wrapper.
 * @param {React.ReactNode} props.children - The avatar content.
 * @param {boolean} [props.hasEditOverlay] - Whether to show the "edit" overlay.
 * @param {string} [props.title] -  An optional title for the avatar, useful for when no additional text is provided will provide a native tooltip on hover
 * @returns {React.ReactElement} The wrapper element.
 */
function AvatarWrapper({ href, className, children, hasEditOverlay, title }: {
    href?: string;
    className: string;
    children: React.ReactNode;
    hasEditOverlay?: boolean;
    title?: string;
}) {
    if (href) {
        return (<a href={href} className={className} title={title}>
                {children}
                {hasEditOverlay && <p className={cx("avatarWrapperEditOverlay", avatarEditStyles)}>Edit</p>}
            </a>);
    }
    return (<div className={className} title={title}>
            {children}
        </div>);
}
/**
 * The base component for rendering an avatar.
 * Handles image loading, fallback rendering, and wrapper behavior.
 *
 * @param {object} props - The component props.
 * @param {AvatarSize} props.size - The size of the avatar (small, medium, large).
 * @param {string} [props.src] - The source URL for the avatar image.
 * @param {string} [props.alt] - The alt text for the avatar image.
 * @param {string} [props.href] - The URL to navigate to when the avatar is clicked.
 * @param {boolean} [props.hasEditOverlay] - Whether to show the "edit" overlay.
 * @param {boolean} [props.isMuted] - Whether the avatar is visually isMuted.
 * @param {React.ReactNode} props.fallback - The fallback content if the image fails to load.
 * @param {AvatarType} props.type - The type of the avatar (user, space, project, etc.).
 * @param {string} [props.additionalStyles] - Additional CSS styles to apply to the avatar e.g. for service user avatars.
 * @param {string} [props.title] -  An optional title for the avatar, useful for when no additional text is provided will provide a native tooltip on hover
 * @returns {React.ReactElement} The rendered avatar.
 */
function AvatarBase({ size, src, srcSet, alt, href, additionalStyles, hasEditOverlay, isMuted, fallback, type, title, }: {
    size: AvatarSize;
    src?: string;
    srcSet?: string;
    alt?: string;
    href?: string;
    additionalStyles?: string;
    hasEditOverlay?: boolean;
    isMuted?: boolean;
    fallback: React.ReactNode;
    type: AvatarType;
    title?: string;
}): React.ReactElement {
    const avatarTypeStyles = getAvatarStyles(type, size);
    const canLoadImage = useCanLoadImage(src || "");
    const optionalImageProps = {
        ...(srcSet && { srcSet }),
    };
    return (<AvatarWrapper href={href} className={cx(rootAvatarStyles, avatarTypeStyles, additionalStyles, { [isMutedAvatarStyles]: isMuted })} hasEditOverlay={hasEditOverlay} title={title}>
            {src && canLoadImage === true ? <img className={avatarImageStyles} src={src} {...optionalImageProps} alt={alt}/> : fallback}
        </AvatarWrapper>);
}
/**
 * Renders an avatar for spaces with an image or fallback content.
 * If the image is not provided, a fallback icon representing a space (Octopus logo) is displayed.
 *
 * @param {AvatarSpaceProps} props - The properties for the space avatar.
 * @param {AvatarSize} props.size - The size of the avatar (small, medium, large).
 * @param {string} [props.src] - The source URL for the avatar image.
 * @param {string} [props.alt] - The alt text for the avatar image.
 * @param {string} [props.href] - The URL to navigate to when the avatar is clicked.
 * @param {boolean} [props.hasEditOverlay] - Whether to display an "edit" overlay on the avatar.
 * @param {boolean} [props.isMuted] - Whether the avatar is visually isMuted.
 * @param {string} [props.text] - The text to display if no image is provided, truncated to 2 characters also used for accessibility.
 * @param {boolean} [props.isServiceUser] - Determines if the user is a service account, which changes the fallback icon.
 * @param {boolean} [props.isAdminOrSystem] - Determines if the user is an admin or system user, which changes the fallback icon.
 * @param {string} [props.title] -  An optional title for the avatar, useful for when no additional text is provided will provide a native tooltip on hover
 * @returns {React.ReactElement} A React element representing the space avatar.
 */
function AvatarUser({ type, size, text, src, alt, isServiceUser = false, href, hasEditOverlay, isMuted, title }: AvatarUserProps): React.ReactElement {
    const iconSize = getIconSize(size);
    const userName = text && text.toLowerCase();
    const isAdminOrSystem = ["admin", "system", "administrator", "sysadmin"].includes(userName);
    const fallback = (() => {
        if (isServiceUser) {
            return <OctopusCogIcon size={iconSize} ariaLabel="Service User Icon"/>;
        }
        if (isAdminOrSystem) {
            return <OctopusIcon size={iconSize} ariaLabel={text}/>;
        }
        if (text) {
            return (<p className={avatarInitials} title={text}>
                    {generateFallbackText(text)}
                </p>);
        }
        return <UserIcon size={iconSize} ariaLabel="User"/>;
    })();
    const { avatarImageUrl, srcSet } = src ? getAvatarImageUrls(src, size) : { avatarImageUrl: undefined, srcSet: "" };
    const optionalImageProps = {
        ...(src && { src: avatarImageUrl }),
        ...(srcSet && { srcSet }),
        ...(alt && { alt }),
    };
    // Additional styles are needed to handle differences in colours, hover and focus states, these only apply to user avatars.
    const serviceOrSystemUser = isServiceUser || isAdminOrSystem || !text;
    const userAdditionalStyles = serviceOrSystemUser ? serviceAndSystemUserAvatarStyles : undefined;
    return <AvatarBase type={type} size={size} {...optionalImageProps} href={href} hasEditOverlay={hasEditOverlay} isMuted={isMuted} fallback={fallback} additionalStyles={userAdditionalStyles} title={title}/>;
}
/**
 * Renders an avatar for spaces with an image or fallback content.
 * If the image is not provided, a fallback icon representing a space (Octopus logo) is displayed.
 *
 * @param {AvatarSpaceProps} props - The properties for the space avatar.
 * @param {AvatarSize} props.size - The size of the avatar (small, medium, large).
 * @param {string} [props.src] - The source URL for the avatar image.
 * @param {string} [props.alt] - The alt text for the avatar image.
 * @param {string} [props.href] - The URL to navigate to when the avatar is clicked.
 * @param {boolean} [props.hasEditOverlay] - Whether to display an "edit" overlay on the avatar.
 * @param {boolean} [props.isMuted] - Whether the avatar is visually isMuted.
 * @param {string} [props.title] -  An optional title for the avatar, useful for when no additional text is provided will provide a native tooltip on hover
 * @returns {React.ReactElement} A React element representing the space avatar.
 */
function AvatarSpace({ size, src, alt, href, hasEditOverlay, isMuted, title }: AvatarSpaceProps): React.ReactElement {
    const iconSize = getIconSize(size);
    const fallback = <OctopusIcon size={iconSize} ariaLabel="Space Icon"/>;
    return <AvatarBase type="space" size={size} src={src} alt={alt ?? "Space Icon"} href={href} hasEditOverlay={hasEditOverlay} isMuted={isMuted} fallback={fallback} title={title}/>;
}
/**
 * Renders an avatar for projects with an image or fallback content.
 * If the image is not provided, a fallback icon (Rocket) is displayed.
 *
 * @param {AvatarProjectProps} props - The properties for the project avatar.
 * @param {AvatarSize} props.size - The size of the avatar (small, medium, large).
 * @param {string} [props.src] - The source URL for the avatar image.
 * @param {string} [props.alt] - The alt text for the avatar image.
 * @param {string} [props.href] - The URL to navigate to when the avatar is clicked.
 * @param {boolean} [props.hasEditOverlay] - Whether to display an "edit" overlay on the avatar.
 * @param {boolean} [props.isMuted] - Whether the avatar is visually isMuted.
 * @param {string} [props.title] -  An optional title for the avatar, useful for when no additional text is provided will provide a native tooltip on hover
 * @returns {React.ReactElement} A React element representing the project avatar.
 */
function AvatarProject({ size, src, alt, href, hasEditOverlay, isMuted, title }: AvatarProjectProps): React.ReactElement {
    const iconSize = getIconSize(size);
    const fallback = <AvatarFallbackProjectIcon size={iconSize}/>;
    return <AvatarBase type="project" size={size} src={src} alt={alt ?? "Project Icon"} href={href} hasEditOverlay={hasEditOverlay} isMuted={isMuted} fallback={fallback} title={title}/>;
}
/**
 * Renders an avatar for tenants with an image or fallback content.
 * If the image is not provided, a fallback icon is displayed.
 *
 * @param {AvatarTenantProps} props - The properties for the tenant avatar.
 * @param {AvatarSize} props.size - The size of the avatar (small, medium, large).
 * @param {string} [props.src] - The source URL for the avatar image.
 * @param {string} [props.alt] - The alt text for the avatar image.
 * @param {string} [props.href] - The URL to navigate to when the avatar is clicked.
 * @param {boolean} [props.hasEditOverlay] - Whether to display an "edit" overlay on the avatar.
 * @param {boolean} [props.isMuted] - Whether the avatar is visually isMuted.
 * @param {string} [props.title] -  An optional title for the avatar, useful for when no additional text is provided will provide a native tooltip on hover
 * @returns {React.ReactElement} A React element representing the tenant avatar.
 */
function AvatarTenant({ size, href, src, alt, hasEditOverlay, isMuted, title }: AvatarTenantProps): React.ReactElement {
    const iconSize = getIconSize(size);
    const fallback = <AvatarFallbackTenantIcon size={iconSize}/>;
    return <AvatarBase type="tenant" size={size} src={src} alt={alt ?? "Tenant Icon"} href={href} hasEditOverlay={hasEditOverlay} isMuted={isMuted} fallback={fallback} title={title}/>;
}
/**
 * Renders an avatar for templates with an image or fallback content.
 * If the image is not provided, a fallback icon (Platform Hub) is displayed.
 *
 * @param {AvatarTemplateProps} props - The properties for the template avatar.
 * @param {AvatarSize} props.size - The size of the avatar (small, medium, large).
 * @param {string} [props.src] - The source URL for the avatar image.
 * @param {string} [props.alt] - The alt text for the avatar image.
 * @param {string} [props.href] - The URL to navigate to when the avatar is clicked.
 * @param {boolean} [props.hasEditOverlay] - Whether to display an "edit" overlay on the avatar.
 * @param {boolean} [props.isMuted] - Whether the avatar is visually isMuted.
 * @param {string} [props.title] -  An optional title for the avatar, useful for when no additional text is provided will provide a native tooltip on hover
 * @returns {React.ReactElement} A React element representing the template avatar.
 */
function AvatarTemplate({ size, src, alt, href, hasEditOverlay, isMuted, title }: AvatarTemplateProps): React.ReactElement {
    const iconSize = getIconSize(size);
    const fallback = <PlatformHubIcon size={iconSize} ariaLabel="Template Icon"/>;
    return <AvatarBase type="template" size={size} src={src} alt={alt ?? "Template Icon"} href={href} hasEditOverlay={hasEditOverlay} isMuted={isMuted} fallback={fallback} title={title}/>;
}
// Shared avatar styles
const rootAvatarStyles = css({
    display: "flex",
    flexShrink: 0,
    userSelect: "none",
    overflow: "hidden",
    alignItems: "center",
    justifyContent: "center",
    containerType: "inline-size",
    textDecoration: "none",
    color: themeTokens.color.avatar.text,
    fontFamily: fontFamily.default,
    position: "relative",
    transition: "background-color 150ms ease-in-out",
    // Note: Have to set the color for hover and focus because of the global LESS files for text colors
    // If we dont set it will inherit from the LESS styles instead of the component
    "&[href]:hover": {
        color: themeTokens.color.avatar.text,
    },
    "&[href]:focus-visible": {
        color: themeTokens.color.avatar.text,
        outline: `2px solid ${themeTokens.color.border.selected}`,
        outlineOffset: "2px",
    },
    // Display Edit text on hover or focus
    "&[href]:hover > .avatarWrapperEditOverlay, &[href]:focus-visible > .avatarWrapperEditOverlay": {
        opacity: "1",
        transition: "opacity 150ms ease-in-out",
    },
    // Different background colour for images so if the image is transparent the background is less intrusive
    "& img": {
        backgroundColor: themeTokens.color.avatar.background.image.default,
        transition: "background-color 150ms ease-in-out",
    },
    "&[href]:hover img": {
        backgroundColor: themeTokens.color.avatar.background.image.hover,
    },
});
// State Styles
const isMutedAvatarStyles = css({
    opacity: 0.4,
});
const avatarEditStyles = css({
    color: themeTokens.color.avatar.text,
    position: "absolute",
    inset: 0,
    display: "grid",
    placeContent: "center",
    overflow: "hidden",
    backgroundColor: themeTokens.color.overlay.primary,
    fontSize: "34cqi",
    containerType: "inline-size",
    margin: 0,
    opacity: 0,
});
// Variant styles
const avatarInitials = css({
    fontSize: "44cqi",
    display: "block",
    margin: 0,
    // Explicitly set hover and focus colors to override global LESS styles.
    color: themeTokens.color.avatar.text,
});
const avatarImageStyles = css({
    objectFit: "scale-down",
    width: "100%",
    height: "100%",
    aspectRatio: "1 / 1",
});
const userAvatarStyles = css({
    borderRadius: borderRadius.circle,
    backgroundColor: themeTokens.color.avatar.background.user.default,
    textTransform: "uppercase",
    fontFamily: fontFamily.default,
    fontWeight: fontWeight[400],
    "&[href]:hover, &[href]:focus-visible": {
        backgroundColor: themeTokens.color.avatar.background.user.hover,
    },
    "&[href]:focus-visible": {
        outline: `2px solid ${themeTokens.color.border.selected}`,
        outlineOffset: "2px",
    },
});
const serviceAndSystemUserAvatarStyles = css({
    backgroundColor: themeTokens.color.avatar.background.image.default,
    color: themeTokens.color.avatar.icon.secondary,
    "&[href]:hover, &[href]:focus-visible": {
        color: themeTokens.color.avatar.icon.secondary,
        backgroundColor: themeTokens.color.avatar.background.image.hover,
    },
});
const entityAvatarStyles = css({
    borderRadius: borderRadius.small,
});
const projectAvatarStyles = css({
    backgroundColor: themeTokens.color.avatar.background.project.default,
    "&[href]:hover, &[href]:focus-visible": {
        backgroundColor: themeTokens.color.avatar.background.project.hover,
    },
});
const spaceAvatarStyles = css({
    backgroundColor: themeTokens.color.avatar.background.space.default,
    "&[href]:hover, &[href]:focus-visible": {
        backgroundColor: themeTokens.color.avatar.background.space.hover,
    },
});
const tenantAvatarStyles = css({
    backgroundColor: themeTokens.color.avatar.background.tenant.default,
    color: themeTokens.color.avatar.icon.secondary,
    "&[href]:hover, &[href]:focus-visible": {
        color: themeTokens.color.avatar.icon.secondary,
        backgroundColor: themeTokens.color.avatar.background.tenant.hover,
    },
});
const templateAvatarStyles = css({
    backgroundColor: themeTokens.color.avatar.background.template.default,
    color: themeTokens.color.avatar.icon.secondary,
    "&[href]:hover, &[href]:focus-visible": {
        color: themeTokens.color.avatar.icon.secondary,
        backgroundColor: themeTokens.color.avatar.background.template.hover,
    },
});
// Maps avatar sizes to their pixel dimensions.
const avatarSizeMap = {
    small: 20,
    medium: 36,
    large: 48,
};
// Maps avatar types to their corresponding styles.
const typeStylesMap = {
    user: userAvatarStyles,
    project: cx(projectAvatarStyles, entityAvatarStyles),
    space: cx(spaceAvatarStyles, entityAvatarStyles),
    tenant: cx(tenantAvatarStyles, entityAvatarStyles),
    template: cx(templateAvatarStyles, entityAvatarStyles),
};
function getAvatarStyles(type: AvatarType, size: AvatarSize) {
    const sizeStyles = css({
        height: `${avatarSizeMap[size]}px`,
        width: `${avatarSizeMap[size]}px`,
    });
    const typeStyles = typeStylesMap[type];
    return cx(sizeStyles, typeStyles);
}
